import { useEffect, useMemo } from 'react'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import submitLoyaltyPointsBurnCalculationRequest from 'actions/loyalty/submitLoyaltyPointsBurnCalculationRequest'
import buildBurnKey from 'loyalty/lib/pointsCalculation/buildBurnKey'

type SearchBurnResult = {
  enabled: false
} | {
  enabled: true
  pointsToBurn: number
  priceInPoints: number
  hideZeroCash: boolean
}

/**
 * Calculates how many points to burn for a given price on the search page,
 * based on the current burn toggle/slider state.
 *
 * Uses the batch burn API for backend-calculated priceInPoints.
 * Returns disabled until the backend result is available.
 */
export default function useSearchBurnPoints(price: number, loyaltyProductType: App.LoyaltyProductType | undefined, mx?: string): SearchBurnResult {
  const dispatch = useAppDispatch()
  const { hasSelectedBurnToggle, burnUserBalance, pointsToBurn: contextPointsToBurn } = useSearchBurnEnabled()
  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const customerId = useAppSelector(state => state.auth.account.memberId ?? '')

  const batchKey = useMemo(() => {
    if (!hasSelectedBurnToggle || !loyaltyProductType) return undefined
    return buildBurnKey({ price, loyaltyProductType, mx }, customerId)
  }, [hasSelectedBurnToggle, loyaltyProductType, price, mx, customerId])

  const batchResult = useAppSelector(state => {
    if (!batchKey) return undefined
    return state.loyalty.pointsBurnBatchCalculations?.[batchKey]?.data
  })

  useEffect(() => {
    if (!hasSelectedBurnToggle || !loyaltyProductType) return
    dispatch(submitLoyaltyPointsBurnCalculationRequest({ price, loyaltyProductType, mx }))
  }, [dispatch, hasSelectedBurnToggle, price, loyaltyProductType, mx])

  return useMemo(() => {
    if (!hasSelectedBurnToggle || !batchResult) return { enabled: false as const }

    const fullConversionPoints = batchResult.priceInPoints
    let pointsToBurn: number
    if (contextPointsToBurn !== undefined) {
      pointsToBurn = Math.min(contextPointsToBurn, fullConversionPoints)
    } else if (burnUserBalance) {
      pointsToBurn = Math.min(userPoints, fullConversionPoints)
    } else {
      pointsToBurn = fullConversionPoints
    }

    const hideZeroCash = contextPointsToBurn !== undefined ? false : !burnUserBalance

    return { enabled: true as const, pointsToBurn, priceInPoints: batchResult.priceInPoints, hideZeroCash }
  }, [hasSelectedBurnToggle, contextPointsToBurn, burnUserBalance, userPoints, batchResult])
}
